// src/pages/ServiceProviderDashboard.tsx: provider management hub
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { supabase } from '../services/supabase';
import LocationSelect from '../components/LocationSelect';
import MarketContext from '../components/MarketContext';
import {
  C, CATEGORIES, REGIONS, PRICE_MODELS, CURRENCIES,
  uploadAsset, signedAssetUrl,
  ServiceProvider, ServiceProduct, ServiceConnection, ServiceMessage,
} from '../lib/serviceConnect';

const card: React.CSSProperties = { background: C.white, border: `1px solid ${C.border}`, borderRadius: '4px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const inputStyle: React.CSSProperties = { width: '100%', background: C.white, border: `1px solid ${C.border}`, borderRadius: '3px', color: C.text, fontSize: '13px', padding: '8px 10px', outline: 'none', fontFamily: 'system-ui', boxSizing: 'border-box' };
const labelStyle: React.CSSProperties = { fontSize: '10px', fontWeight: 700, color: C.muted, letterSpacing: '0.6px', textTransform: 'uppercase', display: 'block', marginBottom: '5px' };
const btnPrimary: React.CSSProperties = { background: C.navy, color: C.white, border: 'none', borderRadius: '3px', padding: '9px 20px', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui' };
const btnGhost: React.CSSProperties = { background: C.white, color: C.textSec, border: `1px solid ${C.border}`, borderRadius: '3px', padding: '8px 16px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'system-ui' };

type Tab = 'overview' | 'profile' | 'products' | 'connections' | 'messages';

export default function ServiceProviderDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  const [provider, setProvider] = useState<ServiceProvider | null>(null);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadErr] = useState('');

  // 'overview' is the param-less landing tab, which keeps the sidebar's
  // Dashboard link (no ?tab=) highlighted when it is active.
  const tab = (searchParams.get('tab') as Tab) || 'overview';
  const setTab = (t: Tab) => setSearchParams(t === 'overview' ? {} : { tab: t });

  useEffect(() => {
    if (!user) return;
    (async () => {
      // limit(1) so an owner with more than one provider row loads the newest
      // instead of erroring out of maybeSingle().
      const { data, error } = await supabase.from('service_providers').select('*')
        .eq('owner_id', user.id).order('created_at', { ascending: false }).limit(1).maybeSingle();
      // Only a clean "no rows" result means they still need onboarding, a failed
      // read must not push an existing provider back through setup.
      if (error) { setLoadErr(error.message); setLoading(false); return; }
      if (!data) { navigate('/service-provider-onboarding', { replace: true }); return; }
      setProvider(data as ServiceProvider);
      setLoading(false);
    })();
  }, [user, navigate]);

  if (loadError) return (
    <div style={{ padding: '48px 32px', background: C.surface, minHeight: '100vh', fontFamily: 'system-ui' }}>
      <div style={{ ...card, padding: '40px', textAlign: 'center', maxWidth: '520px', margin: '0 auto' }}>
        <div style={{ fontFamily: 'Georgia, serif', fontSize: '16px', color: C.text, marginBottom: '8px' }}>Could not load your dashboard</div>
        <div style={{ fontSize: '12.5px', color: C.muted, marginBottom: '20px', lineHeight: 1.6 }}>{loadError}</div>
        <button onClick={() => window.location.reload()} style={btnPrimary}>Try again</button>
      </div>
    </div>
  );

  if (loading || !provider) return (
    <div style={{ display: 'flex', height: '60vh', alignItems: 'center', justifyContent: 'center', background: C.surface }}>
      <div style={{ color: C.muted, fontSize: '13px', fontFamily: 'system-ui' }}>Loading dashboard…</div>
    </div>
  );

  const tabs: [Tab, string][] = [['overview', 'Dashboard'], ['profile', 'Profile'], ['products', 'Products'], ['connections', 'Connections'], ['messages', 'Messages']];

  return (
    <div style={{ padding: '28px 32px', minHeight: '100vh', background: C.surface, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* The overview tab opens with its own welcome card, so the page title
          would only duplicate it. */}
      {tab !== 'overview' && (
        <div style={{ marginBottom: '18px' }}>
          <h1 style={{ fontFamily: 'Georgia, serif', fontSize: '24px', color: C.black, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>My ServiceConnect Profile</h1>
          <p style={{ fontSize: '13px', color: C.muted, margin: 0 }}>Manage your listing, products, connections and conversations</p>
        </div>
      )}

      <div style={{ display: 'flex', gap: '2px', borderBottom: `1px solid ${C.border}`, marginBottom: '22px', flexWrap: 'wrap' }}>
        {tabs.map(([key, lbl]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            background: 'none', border: 'none', cursor: 'pointer', padding: '10px 16px',
            fontSize: '13px', fontFamily: 'system-ui', fontWeight: tab === key ? 600 : 500,
            color: tab === key ? C.navy : C.muted,
            borderBottom: tab === key ? `2px solid ${C.navy}` : '2px solid transparent', marginBottom: '-1px',
          }}>{lbl}</button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab provider={provider} fullName={profile?.full_name || ''} onTab={setTab} />}
      {tab === 'profile' && <ProfileTab provider={provider} onSaved={setProvider} />}
      {tab === 'products' && <ProductsTab providerId={provider.id} />}
      {tab === 'connections' && <ConnectionsTab providerId={provider.id} />}
      {tab === 'messages' && <MessagesTab providerId={provider.id} userId={profile?.id || user!.id} />}
    </div>
  );
}

// ── Overview tab ─────────────────────────────────────────────────────────────
interface OverviewRow extends ServiceConnection { requester_firm: { id: string; name: string } | null; }

// Fields that make a listing worth surfacing to managers. Order is the order
// they are listed back to the provider as "still missing".
const COMPLETION_FIELDS: [keyof ServiceProvider, string][] = [
  ['description',     'Description'],
  ['website',         'Website'],
  ['founded_year',    'Founded year'],
  ['client_count',    'Client count'],
  ['regions_covered', 'Regions covered'],
  ['logo_url',        'Logo'],
];

function isFilled(provider: ServiceProvider, key: keyof ServiceProvider): boolean {
  const v = provider[key];
  if (v == null) return false;
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'string') return v.trim().length > 0;
  return true;
}

const STATUS_STYLE: Record<string, { label: string; color: string; bg: string; bd: string }> = {
  pending:  { label: 'Pending',  color: C.amber,   bg: '#FFFBEB', bd: '#FDE68A' },
  approved: { label: 'Approved', color: C.success, bg: '#F0FDF4', bd: '#BBF7D0' },
  declined: { label: 'Declined', color: C.danger,  bg: '#FEF2F2', bd: '#FECACA' },
};

function StatCard({ label, value, accent, note }: { label: string; value: number | null; accent: string; note?: string }) {
  return (
    <div style={{ ...card, padding: '16px 18px', borderLeft: `3px solid ${accent}` }}>
      <div style={{ ...labelStyle, marginBottom: '8px' }}>{label}</div>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: '28px', color: accent, lineHeight: 1, letterSpacing: '-0.5px', marginBottom: '5px' }}>
        {value == null ? '-' : value}
      </div>
      <div style={{ fontSize: '11px', color: C.mutedLt }}>{note || ' '}</div>
    </div>
  );
}

function OverviewTab({ provider, fullName, onTab }: {
  provider: ServiceProvider; fullName: string; onTab: (t: Tab) => void;
}) {
  const [counts, setCounts] = useState<{ total: number; pending: number; approved: number; products: number } | null>(null);
  const [recent, setRecent] = useState<OverviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError('');
      const conn = () => supabase.from('service_connections').select('*', { count: 'exact', head: true }).eq('provider_id', provider.id);
      const [total, pending, approved, products, recentRes] = await Promise.all([
        conn(),
        conn().eq('status', 'pending'),
        conn().eq('status', 'approved'),
        supabase.from('service_provider_products').select('*', { count: 'exact', head: true }).eq('provider_id', provider.id),
        // service_connections has no created_at column, requested_at is the
        // creation stamp (see 20260719_serviceconnect.sql).
        supabase.from('service_connections').select('*, requester_firm:firms(id, name)')
          .eq('provider_id', provider.id).order('requested_at', { ascending: false }).limit(5),
      ]);
      if (cancelled) return;
      const failed = [total, pending, approved, products, recentRes].find(r => r.error);
      if (failed?.error) { setError(failed.error.message); setLoading(false); return; }
      setCounts({
        total: total.count || 0, pending: pending.count || 0,
        approved: approved.count || 0, products: products.count || 0,
      });
      setRecent((recentRes.data as unknown as OverviewRow[]) || []);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [provider.id]);

  const missing = COMPLETION_FIELDS.filter(([k]) => !isFilled(provider, k));
  const pct = Math.round(((COMPLETION_FIELDS.length - missing.length) / COMPLETION_FIELDS.length) * 100);
  const fmtDate = (ts: string | null) => ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
  const firstName = (fullName || '').trim().split(' ')[0];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', maxWidth: '1100px' }}>

      {/* ── Welcome ── */}
      <div style={{ background: C.navy, borderRadius: '4px', padding: '24px 28px', borderLeft: `3px solid ${C.gold}` }}>
        <div style={{ fontSize: '9.5px', color: C.gold, letterSpacing: '1.6px', fontWeight: 700, textTransform: 'uppercase', marginBottom: '8px' }}>
          Your ServiceConnect Dashboard
        </div>
        <h1 style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontSize: '25px', color: C.white, fontWeight: 400, margin: '0 0 4px', letterSpacing: '-0.3px' }}>
          Welcome back{firstName ? `, ${firstName}` : ''}
        </h1>
        <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>{provider.name}</div>
      </div>

      {error && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', fontSize: '12.5px', color: C.danger }}>{error}</div>
      )}

      {/* ── Metrics, 4 across on desktop, 2x2 on narrow screens ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '12px' }}>
        <StatCard label="Total Connections" value={loading ? null : counts?.total ?? 0} accent={C.navy} note="All statuses" />
        <StatCard label="Pending Requests"  value={loading ? null : counts?.pending ?? 0}
          accent={!loading && (counts?.pending || 0) > 0 ? C.gold : C.muted}
          note={!loading && (counts?.pending || 0) > 0 ? 'Awaiting your response' : 'Nothing waiting'} />
        <StatCard label="Active Clients"    value={loading ? null : counts?.approved ?? 0} accent={C.success} note="Approved connections" />
        <StatCard label="Products Listed"   value={loading ? null : counts?.products ?? 0} accent={C.navy} note="On your profile" />
      </div>

      {/* ── Market context ── */}
      <MarketContext />

      {/* ── Profile completion ── */}
      {pct < 100 && (
        <div style={{ ...card, padding: '20px 24px', borderLeft: `3px solid ${C.gold}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.navy, marginBottom: '2px' }}>Complete your profile to attract more clients</div>
              <div style={{ fontSize: '12px', color: C.muted }}>
                {missing.length} field{missing.length !== 1 ? 's' : ''} still to add
              </div>
            </div>
            <div style={{ fontFamily: 'Georgia, serif', fontSize: '22px', color: C.gold, lineHeight: 1 }}>{pct}%</div>
          </div>
          <div style={{ height: '5px', background: C.borderLt, borderRadius: '3px', overflow: 'hidden', marginBottom: '12px' }}>
            <div style={{ height: '100%', width: `${pct}%`, background: C.gold, transition: 'width 0.25s ease' }} />
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
            {missing.map(([, lbl]) => (
              <span key={lbl} style={{ background: C.surface, border: `1px solid ${C.border}`, color: C.textSec, fontSize: '11.5px', fontWeight: 500, padding: '3px 9px', borderRadius: '2px' }}>{lbl}</span>
            ))}
          </div>
          <button onClick={() => onTab('profile')} style={{ ...btnGhost, padding: '7px 14px' }}>Complete profile →</button>
        </div>
      )}

      {/* ── Recent requests ── */}
      <div style={card}>
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
          <div>
            <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>Recent Connection Requests</div>
            <div style={{ fontSize: '11px', color: C.muted, marginTop: '1px' }}>Your five most recent</div>
          </div>
          <button onClick={() => onTab('connections')} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: C.navy, fontWeight: 600, letterSpacing: '0.2px', fontFamily: 'system-ui' }}>VIEW ALL →</button>
        </div>
        {loading ? (
          <div style={{ padding: '36px', textAlign: 'center', fontSize: '13px', color: C.muted }}>Loading activity…</div>
        ) : recent.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No connection requests yet</div>
            <div style={{ fontSize: '13px', color: C.muted }}>Managers who want to work with you will appear here.</div>
          </div>
        ) : recent.map((r, i) => {
          const s = STATUS_STYLE[r.status] || { label: r.status, color: C.muted, bg: C.surface, bd: C.border };
          return (
            <div key={r.id} style={{
              display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 20px',
              borderBottom: i < recent.length - 1 ? `1px solid ${C.borderLt}` : 'none', flexWrap: 'wrap',
            }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '3px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gold, fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>
                {(r.requester_firm?.name || '?').charAt(0)}
              </div>
              <div style={{ flex: 1, minWidth: '140px' }}>
                <div style={{ fontSize: '13px', fontWeight: 600, color: C.black }}>{r.requester_firm?.name || 'Unknown firm'}</div>
                <div style={{ fontSize: '11px', color: C.muted, marginTop: '1px' }}>Requested {fmtDate(r.requested_at)}</div>
              </div>
              <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.bd}`, fontSize: '9px', fontWeight: 700, padding: '2px 7px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase', flexShrink: 0 }}>{s.label}</span>
              <button onClick={() => onTab('connections')} style={{ ...btnGhost, padding: '5px 12px', flexShrink: 0 }}>View</button>
            </div>
          );
        })}
      </div>

      {/* ── Quick actions ── */}
      <div style={{ ...card, padding: '18px 20px' }}>
        <div style={{ ...labelStyle, marginBottom: '12px' }}>Quick Actions</div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button onClick={() => onTab('profile')} style={btnPrimary}>Edit Profile</button>
          <button onClick={() => onTab('products')} style={btnGhost}>Manage Products</button>
          <button
            onClick={() => window.open(`/service-connect/provider/${provider.id}`, '_blank', 'noopener,noreferrer')}
            style={btnGhost}
          >View Public Profile ↗</button>
        </div>
      </div>
    </div>
  );
}

// ── Profile tab ──────────────────────────────────────────────────────────────
function ProfileTab({ provider, onSaved }: { provider: ServiceProvider; onSaved: (p: ServiceProvider) => void }) {
  const [form, setForm] = useState({
    name: provider.name || '', category: provider.category || CATEGORIES[0],
    description: provider.description || '',
    headquarters_country: provider.headquarters_country || '', headquarters_city: provider.headquarters_city || '',
    regions_covered: provider.regions_covered || [],
    website: provider.website || '', linkedin: provider.linkedin || '',
    founded_year: provider.founded_year ? String(provider.founded_year) : '',
    client_count: provider.client_count != null ? String(provider.client_count) : '',
  });
  const [logoUrl, setLogoUrl]     = useState<string | null>(null);
  const [videoUrl, setVideoUrl]   = useState<string | null>(null);
  const [deckName, setDeckName]   = useState<string | null>(provider.pitch_deck_url ? 'Current pitch deck' : null);
  const [saving, setSaving]       = useState(false);
  const [msg, setMsg]             = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  // pending stored paths for uploaded assets
  const pending = useRef<{ logo_url?: string; pitch_deck_url?: string; demo_video_url?: string }>({});

  useEffect(() => {
    (async () => {
      setLogoUrl(await signedAssetUrl(provider.logo_url));
      setVideoUrl(await signedAssetUrl(provider.demo_video_url, 7200));
    })();
  }, [provider.logo_url, provider.demo_video_url]);

  const toggleRegion = (r: string) => setForm(f => ({
    ...f, regions_covered: f.regions_covered.includes(r) ? f.regions_covered.filter(x => x !== r) : [...f.regions_covered, r],
  }));

  const handleUpload = async (kind: 'logo' | 'deck' | 'video', file: File) => {
    if (!provider.owner_id) return;
    setUploading(kind); setMsg(null);
    try {
      const path = await uploadAsset(provider.owner_id, kind, file);
      if (kind === 'logo') { pending.current.logo_url = path; setLogoUrl(await signedAssetUrl(path)); }
      if (kind === 'deck') { pending.current.pitch_deck_url = path; setDeckName(file.name); }
      if (kind === 'video') { pending.current.demo_video_url = path; setVideoUrl(await signedAssetUrl(path, 7200)); }
    } catch (e: any) { setMsg({ type: 'err', text: `Upload failed: ${e.message}` }); }
    setUploading(null);
  };

  const save = async () => {
    if (!form.name.trim()) { setMsg({ type: 'err', text: 'Name is required.' }); return; }
    setSaving(true); setMsg(null);
    const { data, error } = await supabase.from('service_providers').update({
      name: form.name.trim(), category: form.category,
      description: form.description.trim() || null,
      headquarters_country: form.headquarters_country.trim() || null,
      headquarters_city: form.headquarters_city.trim() || null,
      regions_covered: form.regions_covered,
      website: form.website.trim() || null, linkedin: form.linkedin.trim() || null,
      founded_year: form.founded_year ? parseInt(form.founded_year) : null,
      client_count: form.client_count ? parseInt(form.client_count) : null,
      ...pending.current,
      updated_at: new Date().toISOString(),
    }).eq('id', provider.id).select().single();
    setSaving(false);
    if (error) { setMsg({ type: 'err', text: error.message }); return; }
    pending.current = {};
    onSaved(data as ServiceProvider);
    setMsg({ type: 'ok', text: 'Profile saved.' });
  };

  return (
    <div style={{ ...card, padding: '26px 28px', maxWidth: '780px' }}>
      {msg && <div style={{ background: msg.type === 'ok' ? '#F0FDF4' : '#FEF2F2', border: `1px solid ${msg.type === 'ok' ? '#BBF7D0' : '#FECACA'}`, borderRadius: '3px', padding: '9px 12px', marginBottom: '18px', fontSize: '12.5px', color: msg.type === 'ok' ? C.success : C.danger }}>{msg.text}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div><label style={labelStyle}>Company Name *</label><input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
        <div><label style={labelStyle}>Category *</label>
          <select style={inputStyle} value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>

      <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Description</label>
        <textarea rows={4} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <LocationSelect
          country={form.headquarters_country} city={form.headquarters_city}
          onCountryChange={v => setForm(f => ({ ...f, headquarters_country: v }))}
          onCityChange={v => setForm(f => ({ ...f, headquarters_city: v }))}
          countryLabel="HQ Country" cityLabel="HQ City"
          labelStyle={labelStyle} inputStyle={inputStyle}
        />
      </div>

      <div style={{ marginBottom: '16px' }}><label style={labelStyle}>Regions Covered</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {REGIONS.map(r => {
            const on = form.regions_covered.includes(r);
            return (
              <button key={r} type="button" onClick={() => toggleRegion(r)} style={{
                padding: '6px 12px', borderRadius: '3px', fontSize: '12px', cursor: 'pointer', fontFamily: 'system-ui', fontWeight: 500,
                border: `1px solid ${on ? C.navy : C.border}`, background: on ? C.navy : C.white, color: on ? C.white : C.textSec,
              }}>{r}</button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
        <div><label style={labelStyle}>Website</label><input style={inputStyle} placeholder="acme.com" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} /></div>
        <div><label style={labelStyle}>LinkedIn</label><input style={inputStyle} placeholder="linkedin.com/company/…" value={form.linkedin} onChange={e => setForm(f => ({ ...f, linkedin: e.target.value }))} /></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '22px' }}>
        <div><label style={labelStyle}>Founded Year</label><input style={inputStyle} type="number" value={form.founded_year} onChange={e => setForm(f => ({ ...f, founded_year: e.target.value }))} /></div>
        <div><label style={labelStyle}>Client Count</label><input style={inputStyle} type="number" value={form.client_count} onChange={e => setForm(f => ({ ...f, client_count: e.target.value }))} /></div>
      </div>

      {/* Assets */}
      <div style={{ borderTop: `1px solid ${C.borderLt}`, paddingTop: '20px', marginBottom: '22px' }}>
        <div style={{ ...labelStyle, marginBottom: '14px' }}>Media & Documents</div>
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          {/* Logo */}
          <div>
            <label style={{ ...labelStyle, fontSize: '9.5px' }}>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '52px', height: '52px', borderRadius: '4px', background: logoUrl ? C.white : C.navy, border: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', fontFamily: 'Georgia, serif', color: C.gold, fontSize: '20px', fontWeight: 700 }}>
                {logoUrl ? <img src={logoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : form.name.charAt(0) || '?'}
              </div>
              <UploadButton label={uploading === 'logo' ? 'Uploading…' : 'Upload'} accept="image/*" disabled={uploading === 'logo'} onFile={f => handleUpload('logo', f)} />
            </div>
          </div>
          {/* Deck */}
          <div>
            <label style={{ ...labelStyle, fontSize: '9.5px' }}>Pitch Deck (PDF)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '12px', color: deckName ? C.text : C.mutedLt, maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{deckName || 'No file'}</span>
              <UploadButton label={uploading === 'deck' ? 'Uploading…' : 'Upload'} accept="application/pdf" disabled={uploading === 'deck'} onFile={f => handleUpload('deck', f)} />
            </div>
          </div>
          {/* Video */}
          <div>
            <label style={{ ...labelStyle, fontSize: '9.5px' }}>Demo Video (MP4/MOV)</label>
            <UploadButton label={uploading === 'video' ? 'Uploading…' : 'Upload'} accept="video/mp4,video/quicktime,video/*" disabled={uploading === 'video'} onFile={f => handleUpload('video', f)} />
          </div>
        </div>
        {videoUrl && (
          <div style={{ marginTop: '16px', maxWidth: '480px' }}>
            <video src={videoUrl} controls style={{ width: '100%', borderRadius: '4px', background: C.black, maxHeight: '300px' }} />
          </div>
        )}
      </div>

      <button onClick={save} disabled={saving} style={{ ...btnPrimary, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer', padding: '10px 26px' }}>{saving ? 'Saving…' : 'Save Profile'}</button>
    </div>
  );
}

function UploadButton({ label, accept, disabled, onFile }: { label: string; accept: string; disabled?: boolean; onFile: (f: File) => void }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <button type="button" disabled={disabled} onClick={() => ref.current?.click()} style={{ ...btnGhost, opacity: disabled ? 0.6 : 1, padding: '7px 14px' }}>{label}</button>
      <input ref={ref} type="file" accept={accept} style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
    </>
  );
}

// ── Products tab ─────────────────────────────────────────────────────────────
const emptyProduct = { name: '', description: '', price_from: '', price_to: '', price_model: PRICE_MODELS[0], price_currency: 'USD', is_flagship: false };

function ProductsTab({ providerId }: { providerId: string }) {
  const [products, setProducts] = useState<ServiceProduct[]>([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState<string | null>(null);
  const [form, setForm]         = useState({ ...emptyProduct });
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [providerId]);
  async function load() {
    setLoading(true);
    const { data } = await supabase.from('service_provider_products').select('*').eq('provider_id', providerId).order('is_flagship', { ascending: false }).order('created_at', { ascending: true });
    setProducts((data as ServiceProduct[]) || []);
    setLoading(false);
  }

  const startAdd = () => { setEditing(null); setForm({ ...emptyProduct }); setError(''); setShowForm(true); };
  const startEdit = (p: ServiceProduct) => {
    setEditing(p.id); setError('');
    setForm({ name: p.name, description: p.description || '', price_from: p.price_from || '', price_to: p.price_to || '', price_model: p.price_model || PRICE_MODELS[0], price_currency: p.price_currency || 'USD', is_flagship: p.is_flagship });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true); setError('');
    const payload = {
      provider_id: providerId, name: form.name.trim(), description: form.description.trim() || null,
      price_from: form.price_from.trim() || null, price_to: form.price_to.trim() || null,
      price_model: form.price_model, price_currency: form.price_currency, is_flagship: form.is_flagship,
    };
    // Keep the form open on failure, silently closing it looked like a save.
    const { error: saveErr } = editing
      ? await supabase.from('service_provider_products').update(payload).eq('id', editing)
      : await supabase.from('service_provider_products').insert(payload);
    setSaving(false);
    if (saveErr) { setError(saveErr.message); return; }
    setShowForm(false); setEditing(null); setForm({ ...emptyProduct });
    load();
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this product?')) return;
    const { error: delErr } = await supabase.from('service_provider_products').delete().eq('id', id);
    if (delErr) { setError(delErr.message); return; }
    load();
  };

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading products…</div>;

  return (
    <div style={{ maxWidth: '860px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ fontSize: '12.5px', color: C.muted, fontWeight: 600 }}>{products.length} product{products.length !== 1 ? 's' : ''}</div>
        {!showForm && <button onClick={startAdd} style={btnPrimary}>+ Add Product</button>}
      </div>

      {error && !showForm && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '14px', fontSize: '12.5px', color: C.danger }}>{error}</div>
      )}

      {showForm && (
        <div style={{ ...card, padding: '22px 24px', marginBottom: '18px' }}>
          <div style={{ fontFamily: 'Georgia, serif', fontSize: '15px', color: C.navy, marginBottom: '16px' }}>{editing ? 'Edit Product' : 'New Product'}</div>
          {error && <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: '3px', padding: '9px 12px', marginBottom: '14px', fontSize: '12.5px', color: C.danger }}>{error}</div>}
          <div style={{ marginBottom: '14px' }}><label style={labelStyle}>Name *</label><input style={inputStyle} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} /></div>
          <div style={{ marginBottom: '14px' }}><label style={labelStyle}>Description</label><textarea rows={3} style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginBottom: '14px' }}>
            <div><label style={labelStyle}>Price From</label><input style={inputStyle} placeholder="10,000" value={form.price_from} onChange={e => setForm(f => ({ ...f, price_from: e.target.value }))} /></div>
            <div><label style={labelStyle}>Price To</label><input style={inputStyle} placeholder="50,000" value={form.price_to} onChange={e => setForm(f => ({ ...f, price_to: e.target.value }))} /></div>
            <div><label style={labelStyle}>Currency</label><select style={inputStyle} value={form.price_currency} onChange={e => setForm(f => ({ ...f, price_currency: e.target.value }))}>{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label style={labelStyle}>Model</label><select style={inputStyle} value={form.price_model} onChange={e => setForm(f => ({ ...f, price_model: e.target.value }))}>{PRICE_MODELS.map(m => <option key={m}>{m}</option>)}</select></div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12.5px', color: C.textSec, cursor: 'pointer', marginBottom: '18px' }}>
            <input type="checkbox" checked={form.is_flagship} onChange={e => setForm(f => ({ ...f, is_flagship: e.target.checked }))} />
            Flagship product (highlighted on your profile)
          </label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={save} disabled={saving || !form.name.trim()} style={{ ...btnPrimary, opacity: saving || !form.name.trim() ? 0.6 : 1 }}>{saving ? 'Saving…' : editing ? 'Update' : 'Add Product'}</button>
            <button onClick={() => { setShowForm(false); setEditing(null); setError(''); }} style={btnGhost}>Cancel</button>
          </div>
        </div>
      )}

      {products.length === 0 && !showForm ? (
        <div style={{ ...card, padding: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No products yet</div>
          <div style={{ fontSize: '13px', color: C.muted }}>Add your first offering so managers can see what you provide.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {products.map(p => (
            <div key={p.id} style={{ ...card, padding: '16px 20px', borderLeft: p.is_flagship ? `3px solid ${C.gold}` : `1px solid ${C.border}`, display: 'flex', justifyContent: 'space-between', gap: '14px', alignItems: 'flex-start' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <span style={{ fontFamily: 'Georgia, serif', fontSize: '14.5px', color: C.navy }}>{p.name}</span>
                  {p.is_flagship && <span style={{ background: `${C.gold}22`, color: C.amber, border: `1px solid ${C.gold}44`, fontSize: '8px', fontWeight: 700, padding: '1px 5px', borderRadius: '2px', letterSpacing: '0.5px', textTransform: 'uppercase' }}>Flagship</span>}
                </div>
                {p.description && <div style={{ fontSize: '12px', color: C.textSec, lineHeight: 1.5, marginBottom: '6px' }}>{p.description}</div>}
                <div style={{ fontSize: '12px', color: C.muted }}>
                  {(p.price_from || p.price_to) ? `${p.price_currency || 'USD'} ${p.price_from || ''}${p.price_to ? ` – ${p.price_to}` : ''}` : 'Price on request'}
                  {p.price_model ? ` · ${p.price_model}` : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                <button onClick={() => startEdit(p)} style={{ ...btnGhost, padding: '5px 12px' }}>Edit</button>
                <button onClick={() => remove(p.id)} style={{ ...btnGhost, padding: '5px 12px', color: C.danger, borderColor: `${C.danger}44` }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Connections tab ──────────────────────────────────────────────────────────
interface ConnRow extends ServiceConnection { requester_firm: { id: string; name: string } | null; }

function ConnectionsTab({ providerId }: { providerId: string }) {
  const [rows, setRows]     = useState<ConnRow[]>([]);
  const [loading, setLoad]  = useState(true);
  const [busy, setBusy]     = useState<string | null>(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [providerId]);
  async function load() {
    setLoad(true);
    const { data } = await supabase.from('service_connections')
      .select('*, requester_firm:firms(id, name)')
      .eq('provider_id', providerId).order('requested_at', { ascending: false });
    setRows((data as unknown as ConnRow[]) || []);
    setLoad(false);
  }

  const respond = async (id: string, approve: boolean) => {
    setBusy(id);
    await supabase.from('service_connections').update(
      approve ? { status: 'approved', approved_at: new Date().toISOString() } : { status: 'declined' }
    ).eq('id', id);
    setBusy(null); load();
  };

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading connections…</div>;

  const pending = rows.filter(r => r.status === 'pending');
  const approved = rows.filter(r => r.status === 'approved');
  const fmtDate = (ts: string | null) => ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';

  return (
    <div style={{ maxWidth: '760px', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div>
        <div style={{ ...labelStyle, marginBottom: '12px' }}>Pending Requests ({pending.length})</div>
        {pending.length === 0 ? (
          <div style={{ ...card, padding: '24px', fontSize: '12.5px', color: C.muted, textAlign: 'center' }}>No pending requests.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {pending.map(r => (
              <div key={r.id} style={{ ...card, padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px' }}>
                <div>
                  <div style={{ fontSize: '13.5px', fontWeight: 600, color: C.navy }}>{r.requester_firm?.name || 'Unknown firm'}</div>
                  <div style={{ fontSize: '11.5px', color: C.muted }}>Requested {fmtDate(r.requested_at)}</div>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => respond(r.id, false)} disabled={busy === r.id} style={{ ...btnGhost, color: C.danger, borderColor: `${C.danger}44`, padding: '7px 14px' }}>Decline</button>
                  <button onClick={() => respond(r.id, true)} disabled={busy === r.id} style={{ ...btnPrimary, opacity: busy === r.id ? 0.7 : 1 }}>{busy === r.id ? '…' : 'Approve'}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <div style={{ ...labelStyle, marginBottom: '12px' }}>Approved Connections ({approved.length})</div>
        {approved.length === 0 ? (
          <div style={{ ...card, padding: '24px', fontSize: '12.5px', color: C.muted, textAlign: 'center' }}>No approved connections yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {approved.map(r => (
              <div key={r.id} style={{ ...card, padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '30px', height: '30px', borderRadius: '3px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gold, fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '13px' }}>{(r.requester_firm?.name || '?').charAt(0)}</div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: C.text }}>{r.requester_firm?.name || 'Unknown firm'}</div>
                </div>
                <div style={{ fontSize: '11.5px', color: C.muted }}>Connected {fmtDate(r.approved_at)}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Messages tab ─────────────────────────────────────────────────────────────
interface MsgConn extends ServiceConnection { requester_firm: { id: string; name: string } | null; }

function MessagesTab({ providerId, userId }: { providerId: string; userId: string }) {
  const [conns, setConns]       = useState<MsgConn[]>([]);
  const [active, setActive]     = useState<MsgConn | null>(null);
  const [messages, setMessages] = useState<ServiceMessage[]>([]);
  const [newMsg, setNewMsg]     = useState('');
  const [loading, setLoading]   = useState(true);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [providerId]);
  async function load() {
    setLoading(true);
    const { data } = await supabase.from('service_connections')
      .select('*, requester_firm:firms(id, name)')
      .eq('provider_id', providerId).eq('status', 'approved').order('approved_at', { ascending: false });
    const list = (data as unknown as MsgConn[]) || [];
    setConns(list);
    if (list.length) setActive(prev => prev || list[0]);
    setLoading(false);
  }

  useEffect(() => {
    if (!active) return;
    fetchMessages(active.id);
    const channel = supabase.channel(`sp-dash-msg-${active.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'service_messages', filter: `connection_id=eq.${active.id}` },
        async payload => {
          const m = payload.new as ServiceMessage;
          setMessages(prev => prev.some(x => x.id === m.id) ? prev : [...prev, m]);
          if (m.sender_id !== userId) await supabase.from('service_messages').update({ read_at: new Date().toISOString() }).eq('id', m.id);
        })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
    // eslint-disable-next-line
  }, [active?.id]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function fetchMessages(cid: string) {
    const { data } = await supabase.from('service_messages').select('*').eq('connection_id', cid).order('created_at', { ascending: true });
    setMessages((data as ServiceMessage[]) || []);
    await supabase.from('service_messages').update({ read_at: new Date().toISOString() }).eq('connection_id', cid).neq('sender_id', userId).is('read_at', null);
  }

  const send = async () => {
    if (!newMsg.trim() || !active) return;
    const content = newMsg.trim(); setNewMsg('');
    const { data } = await supabase.from('service_messages').insert({ connection_id: active.id, sender_id: userId, content }).select().single();
    if (data) setMessages(prev => [...prev, data as ServiceMessage]);
  };

  const fmt = (ts: string) => new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (loading) return <div style={{ color: C.muted, fontSize: '13px' }}>Loading messages…</div>;
  if (!conns.length) return (
    <div style={{ ...card, padding: '40px', textAlign: 'center', maxWidth: '600px' }}>
      <div style={{ fontSize: '14px', fontWeight: 600, color: C.text, marginBottom: '6px' }}>No conversations yet</div>
      <div style={{ fontSize: '13px', color: C.muted }}>Approve a connection request to start messaging.</div>
    </div>
  );

  return (
    <div style={{ ...card, display: 'flex', height: '560px', overflow: 'hidden', maxWidth: '960px' }}>
      {/* list */}
      <div style={{ width: '240px', borderRight: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column' }}>
        {conns.map(c => {
          const on = active?.id === c.id;
          return (
            <div key={c.id} onClick={() => setActive(c)} style={{ padding: '12px 14px', cursor: 'pointer', background: on ? '#EBF0FA' : C.white, borderBottom: `1px solid ${C.borderLt}`, borderLeft: on ? `3px solid ${C.navy}` : '3px solid transparent', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '30px', height: '30px', borderRadius: '3px', background: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.gold, fontFamily: 'Georgia, serif', fontWeight: 700, fontSize: '13px', flexShrink: 0 }}>{(c.requester_firm?.name || '?').charAt(0)}</div>
              <div style={{ fontSize: '12.5px', fontWeight: on ? 600 : 500, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.requester_firm?.name || 'Unknown firm'}</div>
            </div>
          );
        })}
      </div>
      {/* thread */}
      {active && (
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '13px 20px', borderBottom: `1px solid ${C.border}`, fontSize: '13px', fontWeight: 600, color: C.navy }}>{active.requester_firm?.name || 'Unknown firm'}</div>
          <div style={{ flex: 1, padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '10px', background: C.surface }}>
            {messages.length === 0 && <div style={{ textAlign: 'center', color: C.muted, fontSize: '13px', marginTop: '40px' }}>No messages yet.</div>}
            {messages.map(m => {
              const isMe = m.sender_id === userId;
              return (
                <div key={m.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '62%', padding: '10px 13px', borderRadius: '4px', background: isMe ? C.navy : C.white, color: isMe ? C.white : C.text, fontSize: '13px', lineHeight: 1.55, border: isMe ? 'none' : `1px solid ${C.border}`, boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                    <div>{m.content}</div>
                    <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.55, textAlign: 'right', fontFamily: '"Courier New", monospace' }}>{fmt(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>
          <div style={{ padding: '12px 20px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '8px' }}>
            <input value={newMsg} onChange={e => setNewMsg(e.target.value)} onKeyDown={e => e.key === 'Enter' && !e.shiftKey && send()} placeholder="Type a message…" style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: '3px', color: C.text, fontSize: '13px', padding: '9px 12px', outline: 'none', fontFamily: 'system-ui' }} onFocus={e => (e.target.style.borderColor = C.navy)} onBlur={e => (e.target.style.borderColor = C.border)} />
            <button onClick={send} style={{ ...btnPrimary, padding: '9px 18px' }}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
}
